import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api, { API, humanizeServiceError } from "../lib/api";
import { LoadErrorBox } from "../components/common/LoadErrorBox";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Table, TableHeader, TableRow, TableHead, TableBody, TableCell,
} from "../components/ui/table";
import { Button } from "../components/ui/button";
import { StatusBadge } from "../components/StatusBadge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "../components/ui/dialog";
import { Download, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { DateInputIT } from "../components/common/DateTimeIT";

import { ResultVies } from "../components/result/ResultVies";
import { ResultCompany } from "../components/result/ResultCompany";
import { ResultVisuraCamerale } from "../components/result/ResultVisuraCamerale";
import { ResultCatastoPG } from "../components/result/ResultCatastoPG";
import { ResultCatastoPF } from "../components/result/ResultCatastoPF";
import { ResultCatastoIndirizzo } from "../components/result/ResultCatastoIndirizzo";
import { ResultVisuraCatastaleUfficiale } from "../components/result/ResultVisuraCatastaleUfficiale";
import { ResultBilancio } from "../components/result/ResultBilancio";

const VISURE_CAMERALI = new Set([
  "visura_ord_societa_capitali",
  "visura_ord_societa_persone",
  "visura_ord_impresa_individuale",
]);

const VISURE_CATASTALI_UFFICIALI = new Set([
  "visura_catastale_immobile_pdf",
  "visura_catastale_soggetto_pg_pdf",
  "visura_catastale_soggetto_pf_pdf",
  "planimetria_catastale_pdf",
]);

const SERVICE_CATEGORY = {
  vies_check: "vies",
  company_it_start: "company",
  company_it_advanced: "company",
  visura_ord_societa_capitali: "visure",
  visura_ord_societa_persone: "visure",
  visura_ord_impresa_individuale: "visure",
  bilancio_ottico: "bilancio",
  catasto_ricerca_persona_giuridica: "catasto",
  catasto_ricerca_persona_fisica: "catasto",
  catasto_ricerca_indirizzo: "catasto",
  visura_catastale_immobile_pdf: "catasto-documenti",
  visura_catastale_soggetto_pg_pdf: "catasto-documenti",
  visura_catastale_soggetto_pf_pdf: "catasto-documenti",
  planimetria_catastale_pdf: "catasto-documenti",
};

const PARAM_LABELS = {
  cf_piva_id: "P.IVA",
  cf_id: "C.F.",
  vat_number: "P.IVA",
  country_code: "Paese",
  anno_chiusura: "Anno",
  indirizzo: "Indirizzo",
  id_indirizzo: "Indirizzo",
  comune: "Comune",
  provincia: "Prov.",
  civico: "Civico",
  foglio: "Foglio",
  particella: "Particella",
  subalterno: "Sub.",
  sezione: "Sezione",
};

const formatParamsHuman = (params) => {
  if (!params || typeof params !== "object") return "—";
  const entries = Object.entries(params).filter(([, v]) => v !== null && v !== undefined && v !== "");
  if (entries.length === 0) return "—";
  return entries
    .slice(0, 3)
    .map(([k, v]) => {
      const label = PARAM_LABELS[k] || k;
      let val = String(v);
      if (k === "anno_chiusura" && (val === "ultimo" || val === "")) val = "Ultimo disponibile";
      return `${label}: ${val}`;
    })
    .join(" · ");
};

/** Render the proper Result component for a completed request.
 *  Returns null if no mapping exists — caller falls back to params/error views. */
const renderResultComponent = (request, { onNewSearch, onUpdate, onManualRefresh }) => {
  if (!request) return null;
  const sid = request.service_id;
  const completed = request.status === "completed";

  // Catasto Indirizzo handles its own internal states (pending/step1/step2)
  if (sid === "catasto_ricerca_indirizzo") {
    return (
      <ResultCatastoIndirizzo
        request={request}
        onNewSearch={onNewSearch}
        onManualRefresh={onManualRefresh}
        onConfirmSelection={() => toast.info(
          "Dettaglio singolo immobile in arrivo. I dati base sono già visibili nella lista qui sopra."
        )}
      />
    );
  }
  // Visure Camerali handles pending_external internally
  if (VISURE_CAMERALI.has(sid)) {
    return <ResultVisuraCamerale request={request} onUpdate={onUpdate} onNewSearch={onNewSearch} />;
  }
  // Visure Catastali UFFICIALI (PDF AdE) handles pending internally
  if (VISURE_CATASTALI_UFFICIALI.has(sid)) {
    return <ResultVisuraCatastaleUfficiale request={request} onUpdate={onUpdate} onNewSearch={onNewSearch} />;
  }
  // Bilancio aziendale (multi-allegato) handles pending internally
  if (sid === "bilancio_ottico") {
    return <ResultBilancio request={request} onUpdate={onUpdate} onNewSearch={onNewSearch} />;
  }
  // Other services require completed status
  if (!completed) return null;
  if (sid === "catasto_ricerca_persona_fisica") return <ResultCatastoPF request={request} onNewSearch={onNewSearch} />;
  if (sid === "catasto_ricerca_persona_giuridica") return <ResultCatastoPG request={request} onNewSearch={onNewSearch} />;
  if (sid === "company_it_start" || sid === "company_it_advanced") return <ResultCompany request={request} onNewSearch={onNewSearch} />;
  if (sid === "vies_check") return <ResultVies request={request} onNewSearch={onNewSearch} onRetry={onNewSearch} />;
  return null;
};

export default function History() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [services, setServices] = useState([]);
  const [filters, setFilters] = useState({ service_id: "all", status: "all", date_from: "", date_to: "" });
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);

  const [loadFailed, setLoadFailed] = useState(false);
  const load = async () => {
    setLoading(true);
    const params = {};
    if (filters.service_id !== "all") params.service_id = filters.service_id;
    if (filters.status !== "all") params.status = filters.status;
    if (filters.date_from) params.date_from = filters.date_from;
    if (filters.date_to) params.date_to = filters.date_to;
    try {
      const { data } = await api.get("/requests/me", { params });
      setRows(data);
      setLoadFailed(false);
    } catch {
      setLoadFailed(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    api.get("/services").then(({ data }) => setServices(data));
  }, []);
  useEffect(() => { load(); }, [filters]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-refresh selected detail when pending_external (10s)
  const pollRef = useRef(null);
  useEffect(() => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    if (selected?.status === "pending_external") {
      pollRef.current = setInterval(async () => {
        try {
          const { data } = await api.get(`/requests/${selected.id}`);
          setSelected(data);
          if (data.status !== "pending_external") {
            clearInterval(pollRef.current);
            pollRef.current = null;
            load();  // refresh table too
          }
        } catch { /* swallow */ }
      }, 10000);
    }
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
    // eslint-disable-next-line
  }, [selected?.status, selected?.id]);

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-3xl tracking-tight font-medium text-text-primary">Storico richieste</h1>
        <p className="text-sm text-text-secondary mt-1">Le verifiche che hai eseguito.</p>
      </div>

      <Card className="bg-surface border border-border rounded-xl shadow-sm p-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="space-y-1.5">
            <Label className="text-xs font-medium text-text-secondary">Servizio</Label>
            <Select value={filters.service_id} onValueChange={(v) => setFilters({ ...filters, service_id: v })}>
              <SelectTrigger data-testid="filter-service" className="h-10 rounded-lg border-border bg-white">
                <SelectValue placeholder="Tutti" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutti</SelectItem>
                {services.map((s) => <SelectItem key={s.service_id} value={s.service_id}>{s.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-medium text-text-secondary">Stato</Label>
            <Select value={filters.status} onValueChange={(v) => setFilters({ ...filters, status: v })}>
              <SelectTrigger data-testid="filter-status" className="h-10 rounded-lg border-border bg-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutti</SelectItem>
                <SelectItem value="completed">Completata</SelectItem>
                <SelectItem value="failed">Fallita</SelectItem>
                <SelectItem value="pending_confirmation">In attesa</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-medium text-text-secondary">Dal</Label>
            <DateInputIT testId="filter-date-from"
              value={filters.date_from} onChange={(v) => setFilters({ ...filters, date_from: v })}
              className="h-10 rounded-lg" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-medium text-text-secondary">Al</Label>
            <DateInputIT testId="filter-date-to" min={filters.date_from}
              value={filters.date_to} onChange={(v) => setFilters({ ...filters, date_to: v })}
              className="h-10 rounded-lg" />
          </div>
        </div>
      </Card>

      <Card className="bg-surface border border-border rounded-xl shadow-sm overflow-hidden">
        <Table data-testid="history-table">
          <TableHeader>
            <TableRow className="bg-subtle hover:bg-subtle">
              <TableHead>Data</TableHead>
              <TableHead>Servizio</TableHead>
              <TableHead>Parametri</TableHead>
              <TableHead>Stato</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={4} className="text-center text-text-muted py-8 text-sm">Caricamento…</TableCell></TableRow>
            ) : loadFailed ? (
              <TableRow><TableCell colSpan={4}><LoadErrorBox what="lo storico richieste" onRetry={load} testId="history-load-error" /></TableCell></TableRow>
            ) : rows.length === 0 ? (
              <TableRow><TableCell colSpan={4} className="text-center text-text-muted py-12 text-sm">Nessuna richiesta trovata</TableCell></TableRow>
            ) : rows.map((r) => (
              <TableRow key={r.id} className="cursor-pointer hover:bg-subtle/60"
                onClick={() => setSelected(r)} data-testid={`history-row-${r.id}`}>
                <TableCell className="text-sm text-text-secondary">{new Date(r.created_at).toLocaleString("it-IT")}</TableCell>
                <TableCell className="text-sm text-text-primary font-medium">{r.service_name}</TableCell>
                <TableCell className="text-xs text-text-secondary max-w-md truncate">{formatParamsHuman(r.params)}</TableCell>
                <TableCell><StatusBadge status={r.status} cacheHit={r.cache_hit} /></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={!!selected} onOpenChange={() => setSelected(null)}>
        <DialogContent className="max-w-5xl w-[95vw] max-h-[92vh] overflow-y-auto rounded-xl" data-testid="history-detail-dialog">
          <DialogHeader>
            <DialogTitle className="text-lg font-medium">{selected?.service_name}</DialogTitle>
            <DialogDescription className="sr-only">
              Dettaglio della richiesta {selected?.service_name} del {selected?.created_at && new Date(selected.created_at).toLocaleString("it-IT")}
            </DialogDescription>
          </DialogHeader>
          {selected && (() => {
            const resultEl = renderResultComponent(selected, {
              onNewSearch: () => {
                setSelected(null);
                const cat = SERVICE_CATEGORY[selected.service_id];
                if (cat) navigate(`/search/${cat}`);
              },
              onUpdate: setSelected,
              onManualRefresh: async () => {
                try { const { data } = await api.get(`/requests/${selected.id}`); setSelected(data); } catch { /* noop */ }
              },
            });

            return (
              <div className="space-y-4">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div className="flex items-center gap-3 text-xs text-text-secondary">
                    <span>{new Date(selected.created_at).toLocaleString("it-IT")}</span>
                    <StatusBadge status={selected.status} cacheHit={selected.cache_hit} />
                    {selected.status === "pending_external" && (
                      <span className="text-xs text-warning-strong flex items-center gap-1.5">
                        <Loader2 size={12} className="animate-spin" />
                        {selected.openapi_state || "In lavorazione"}
                      </span>
                    )}
                  </div>
                  {selected.status === "completed" && selected.pdf_url && !resultEl && (
                    <Button size="sm" data-testid="history-download-pdf"
                      onClick={async () => {
                        try {
                          const token = localStorage.getItem("wattline_token");
                          const res = await fetch(`${API}/requests/${selected.id}/pdf`, {
                            headers: { Authorization: `Bearer ${token}` },
                          });
                          if (!res.ok) {
                            let detail = "Errore download PDF";
                            try { const j = await res.json(); if (j?.detail) detail = j.detail; } catch { /* */ }
                            toast.error(detail);
                            return;
                          }
                          const blob = await res.blob();
                          const url = URL.createObjectURL(blob);
                          const a = document.createElement("a");
                          a.href = url;
                          a.download = selected.pdf_filename || `visura_${selected.id}.pdf`;
                          a.click();
                          URL.revokeObjectURL(url);
                        } catch (e) {
                          toast.error(`Errore download PDF: ${e?.message || e}`);
                        }
                      }}
                      className="rounded-lg bg-brand hover:bg-brand-hover text-white">
                      <Download size={14} className="mr-1.5" /> Scarica PDF
                    </Button>
                  )}
                </div>

                {selected.status === "failed" ? (
                  <>
                    <div>
                      <div className="text-[11px] uppercase tracking-widest text-text-muted mb-1.5">Parametri</div>
                      <pre className="bg-subtle border border-border rounded-lg p-3 text-xs font-mono overflow-x-auto">
                        {JSON.stringify(selected.params, null, 2)}
                      </pre>
                    </div>
                    <div>
                      <div className="text-[11px] uppercase tracking-widest text-text-muted mb-1.5">Errore</div>
                      <div className="bg-danger-subtle border border-danger-subtle rounded-lg p-3 text-sm text-danger-strong">
                        {humanizeServiceError(selected.error_message, selected.service_id) || selected.error_message || "Richiesta fallita."}
                      </div>
                    </div>
                  </>
                ) : resultEl ? (
                  // Render the design-pass component (VIES, Company, Visura Camerale, Catasto…)
                  resultEl
                ) : (
                  // Generic fallback for unknown service_id / unmapped status — never raw JSON
                  <>
                    <div>
                      <div className="text-[11px] uppercase tracking-widest text-text-muted mb-1.5">Parametri</div>
                      <pre className="bg-subtle border border-border rounded-lg p-3 text-xs font-mono overflow-x-auto">
                        {JSON.stringify(selected.params, null, 2)}
                      </pre>
                    </div>
                    <div className="bg-subtle border border-border rounded-lg p-4 text-sm text-text-secondary text-center">
                      {selected.status === "pending_external"
                        ? "Richiesta in lavorazione su OpenAPI.it. Aggiornamento automatico ogni 10 secondi."
                        : "Risultato non disponibile per questo tipo di richiesta."}
                    </div>
                  </>
                )}
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>
    </div>
  );
}
